"use client";

import { FormEvent, useState } from "react";
import { LanguageText } from "./LanguageText";

type QueueSubmission = {
  id: string;
  receiptId: string;
  submissionType: string;
  submitterName: string;
  institution: string;
  submitterEmail: string;
  orcid: string;
  title: string;
  species: string;
  strain: string;
  paperIdentifier: string;
  paperUrl: string;
  correctionTarget: string;
  repositoryAccessions: string;
  repositoryUrl: string;
  sequencingMethod: string;
  referenceGenome: string;
  processedLayer: string;
  processedUrl: string;
  codeUrl: string;
  licence: string;
  notes: string;
  metadataFormat: string;
  metadataText: string;
  status: string;
  publicReviewNote: string;
  createdAt: string;
  updatedAt: string;
};

const nextStatuses: Record<string, string[]> = {
  submitted: ["in-review", "rejected"],
  "in-review": ["changes-requested", "approved", "rejected"],
  "changes-requested": ["in-review", "rejected"],
  approved: ["in-review", "published"],
  rejected: ["in-review"],
  published: [],
};

function apiMessage(value: unknown, fallback: string) {
  if (
    value &&
    typeof value === "object" &&
    "error" in value &&
    typeof value.error === "string"
  ) {
    return value.error;
  }
  return fallback;
}

export function SubmissionAdmin() {
  const [token, setToken] = useState(() => {
    try {
      return window.sessionStorage.getItem("trypcell-admin-token") || "";
    } catch {
      return "";
    }
  });
  const [submissions, setSubmissions] = useState<QueueSubmission[]>([]);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function loadQueue(event?: FormEvent) {
    event?.preventDefault();
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const response = await fetch("/api/admin/queue", {
        headers: { authorization: `Bearer ${token}` },
        cache: "no-store",
      });
      const body = (await response.json()) as {
        submissions?: QueueSubmission[];
        error?: string;
      };
      if (!response.ok || !body.submissions) {
        throw new Error(apiMessage(body, "Unable to load the review queue."));
      }
      setSubmissions(body.submissions);
      window.sessionStorage.setItem("trypcell-admin-token", token);
      setMessage(`Loaded ${body.submissions.length} submission(s).`);
    } catch (reason) {
      setSubmissions([]);
      setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      setBusy(false);
    }
  }

  async function review(event: FormEvent<HTMLFormElement>, receiptId: string) {
    event.preventDefault();
    setBusy(true);
    setError("");
    setMessage("");
    const data = new FormData(event.currentTarget);
    const toStatus = String(data.get("toStatus") || "");
    if (
      toStatus === "published" &&
      !window.confirm(
        "This marks the submission as published. Continue only after the versioned public record is actually released.",
      )
    ) {
      setBusy(false);
      return;
    }
    try {
      const response = await fetch("/api/admin/review", {
        method: "POST",
        headers: {
          authorization: `Bearer ${token}`,
          "content-type": "application/json",
        },
        body: JSON.stringify({
          receiptId,
          toStatus,
          internalComment: String(data.get("internalComment") || "").trim(),
          publicReviewNote: String(data.get("publicReviewNote") || "").trim(),
        }),
      });
      const body = (await response.json()) as { error?: string };
      if (!response.ok) {
        throw new Error(apiMessage(body, "The review decision was not saved."));
      }
      setMessage(`${receiptId} updated to ${toStatus}.`);
      await loadQueue();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
      setBusy(false);
    }
  }

  function forgetToken() {
    try {
      window.sessionStorage.removeItem("trypcell-admin-token");
    } catch {
      // The visible field is still cleared when storage is unavailable.
    }
    setToken("");
    setSubmissions([]);
    setMessage("");
    setError("");
  }

  return (
    <section className="section submission-admin">
      <header>
        <p className="eyebrow">PRIVATE REVIEW QUEUE · NO PUBLIC INDEXING</p>
        <LanguageText
          as="h1"
          zh="提交审核工作区"
          en="Submission review workspace"
        />
        <LanguageText
          as="p"
          zh="此页面不在导航和站点地图中。管理员令牌只保存在当前浏览器标签会话，关闭标签后应重新输入。"
          en="This page is absent from navigation and the sitemap. The administrator token is kept only for this browser-tab session and should be entered again after the tab is closed."
        />
      </header>

      <form className="admin-auth-panel" onSubmit={loadQueue}>
        <label>
          Administrator token · 管理员令牌
          <input
            type="password"
            value={token}
            onChange={(event) => setToken(event.currentTarget.value)}
            autoComplete="current-password"
            required
          />
        </label>
        <button className="button button-dark" type="submit" disabled={busy}>
          {busy ? "Loading…" : "Open private queue"}
        </button>
        <button className="button" type="button" onClick={forgetToken}>
          Forget token
        </button>
      </form>

      {message ? <p className="workflow-message success">{message}</p> : null}
      {error ? (
        <p className="workflow-message error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="admin-queue">
        {submissions.map((submission) => (
          <article key={submission.id}>
            <header>
              <div>
                <span>
                  {submission.submissionType.replaceAll("_", " ")} ·{" "}
                  {submission.receiptId}
                </span>
                <h2>{submission.title}</h2>
                <p>
                  <i>{submission.species}</i>
                  {submission.strain ? ` · ${submission.strain}` : ""}
                </p>
              </div>
              <strong>{submission.status}</strong>
            </header>

            <dl>
              {[
                ["Submitter", submission.submitterName],
                ["Institution", submission.institution],
                ["Email", submission.submitterEmail],
                ["ORCID", submission.orcid],
                ["Paper ID", submission.paperIdentifier],
                ["Paper", submission.paperUrl],
                ["Correction target", submission.correctionTarget],
                ["Accessions", submission.repositoryAccessions],
                ["Repository", submission.repositoryUrl],
                ["Method", submission.sequencingMethod],
                ["Reference", submission.referenceGenome],
                ["Processed layer", submission.processedLayer],
                ["Processed data", submission.processedUrl],
                ["Code", submission.codeUrl],
                ["Licence", submission.licence],
                ["Notes", submission.notes],
                ["Created", submission.createdAt],
                ["Updated", submission.updatedAt],
              ]
                .filter(([, value]) => Boolean(value))
                .map(([label, value]) => (
                  <div key={`${submission.id}-${label}`}>
                    <dt>{label}</dt>
                    <dd>
                      {/^https?:\/\//.test(value) ? (
                        <a href={value} target="_blank" rel="noreferrer">
                          {value} ↗
                        </a>
                      ) : (
                        value
                      )}
                    </dd>
                  </div>
                ))}
            </dl>

            {submission.metadataText ? (
              <details>
                <summary>
                  Submitted {submission.metadataFormat.toUpperCase()} metadata
                </summary>
                <pre>{submission.metadataText}</pre>
              </details>
            ) : null}

            {nextStatuses[submission.status]?.length ? (
              <form
                className="admin-review-form"
                onSubmit={(event) => review(event, submission.receiptId)}
              >
                <label>
                  Next state
                  <select name="toStatus" required defaultValue="">
                    <option value="" disabled>
                      Select…
                    </option>
                    {nextStatuses[submission.status].map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Internal review note
                  <textarea name="internalComment" rows={3} maxLength={1800} />
                </label>
                <label>
                  Contributor-visible note
                  <textarea name="publicReviewNote" rows={3} maxLength={1200} />
                </label>
                <button className="button button-dark" type="submit" disabled={busy}>
                  Save reviewed state
                </button>
              </form>
            ) : (
              <p className="admin-terminal-state">
                Published is terminal. Correct the public record through a new
                correction submission and versioned release.
              </p>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}
